/**
 * F5-R6b — Chat v2 «Mensajería del equipo» (dibujo del CEO, 2026-08-29).
 *
 * El cajón de 420px murió: el chat es una superficie de tres columnas —
 * conversaciones a la izquierda, el hilo al centro, el contexto del caso a la
 * derecha — montada como overlay a pantalla (los cinco shells siguen abriendo
 * el chat por el MISMO botón; ninguna ruta nueva, ningún shell tocado).
 *
 * Qué es real y de dónde sale (tabla completa en el reporte R6b):
 *  - hilos/mensajes/no-leídos: endpoints vivos (polling 20s/15s/30s conservado)
 *  - visto con hora: chat_thread_reads (el server ahora lo expone en messages)
 *  - reacciones: chat_message_reactions (mig 080, append-only)
 *  - sumar gente + «ve desde que se sumó»: chat_thread_joins (mig 080)
 *  - caso vinculado: related_client_id/related_assignment_id (existían desde
 *    la 015 y la UI jamás los mandó — el hallazgo del propio dibujo)
 *  - presencia: efímera en memoria del server (manual; el enganche con la
 *    llamada real es NECESITA-SERVER)
 *  - silenciar: por dispositivo (localStorage) — mute server es NECESITA-SERVER
 *
 * Enter envía · Shift+Enter baja de línea · contador de no leídos en teal
 * (un mensaje sin leer no es un error) · azul #3aa4de SOLO en el recibo de
 * lectura (la única excepción de paleta, declarada en el dibujo).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Plus,
  BellOff,
  Bell,
  ExternalLink,
  Link2,
  AtSign,
  TriangleAlert,
  ChevronLeft,
  Lock,
  Check,
  Minus,
  CircleMinus,
  Info,
} from 'lucide-react';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { useAuthStore } from '@/stores/auth.store';
import { apiCall } from '@/lib/api-client';
import { clientsApi } from '@/features/clients/clients.api';
import {
  HOY_PAGINAS_INICIO, hoyLocal, navigatorApi, useCaseLabels, type HoyPedido,
} from '@/features/navigator/navigator.api';
import {
  useChatUi,
  useChatThreads,
  useChatMessages,
  useChatRecipients,
  useSendMessage,
  useCreateThread,
  useUnreadCount,
  usePresenceMap,
  useSetPresence,
  useReact,
  useAddParticipant,
  useLinkCase,
  useChatMutes,
  type ThreadRow,
  type ChatPrefill,
  type Recipient,
  type ChatMessage,
  type ThreadMessagesPayload,
  type ThreadReaction,
  type PresenceState,
} from '@/features/chat/chatStore';
import { EmptyState, ErrorState, Skeleton } from '@/components/chassis/States';
import { useT, useLang, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

// ===== helpers =====

const NY = 'America/New_York';

function nyTime(iso: string, lang: 'en' | 'es'): string {
  try {
    return new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: NY,
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

function nyDateKey(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: NY }).format(new Date(iso));
  } catch {
    return '';
  }
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function roleKey(role: string): StringKey {
  switch (role) {
    case 'supervisor':
      return 'f5r6b.role.supervisor';
    case 'admin':
      return 'f5r6b.role.admin';
    case 'uploader':
      return 'f5r6b.role.uploader';
    case 'navigator':
      return 'f5r6b.role.navigator';
    default:
      return 'f5r6b.role.agent';
  }
}

/** «Exige respuesta» — mención heurística: @ + mi nombre (o @vos/@you).
 * Heurística declarada: no existe modelo de menciones server-side. */
function mentionsMe(body: string | null, fullName: string): boolean {
  if (!body || !body.includes('@')) return false;
  const b = body.toLowerCase();
  if (b.includes('@vos') || b.includes('@you') || b.includes('@todos')) return true;
  return fullName
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.length >= 3)
    .some((w) => b.includes('@' + w));
}

function isEscalated(subject: string): boolean {
  return /^escalad/i.test(subject) || /^escalat/i.test(subject);
}

interface ThreadDisplay {
  name: string;
  initials: string;
  isCase: boolean;
  isGroup: boolean;
  tag: 'case' | 'group' | 'esc' | null;
  otherId: string | null; // 1:1 → el otro (para el punto de presencia)
}

function threadDisplay(t: ThreadRow, meId: string, names: Map<string, { name: string; role: string }>): ThreadDisplay {
  const isCase = !!t.related_client_id || !!t.related_assignment_id;
  const others = (t.participants ?? []).filter((p) => String(p) !== String(meId));
  const isGroup = (t.participants ?? []).length > 2;
  let name = t.subject;
  if (!isCase && !isGroup && others.length === 1) {
    const o = names.get(String(others[0]));
    if (o) name = o.name;
  }
  const esc = isEscalated(t.subject);
  return {
    name,
    initials: initialsOf(name),
    isCase,
    isGroup,
    tag: esc ? 'esc' : isCase ? 'case' : isGroup ? 'group' : null,
    otherId: !isGroup && others.length === 1 ? String(others[0]) : null,
  };
}

const PRESENCE_DOT: Record<PresenceState, string> = {
  free: 'bg-nav-sage',
  call: 'bg-nav-teal',
  off: 'bg-nav-line',
};

// Las 5 reacciones rápidas del dibujo.
const QUICK_SET = ['👍', '🙌', '✅', '❤️', '😂'];

// Bandeja del dibujo: seis categorías, «trabajo y casos» y «salud y hogar»
// propias de este producto.
const EMOJI_SETS: Array<{ key: string; icon: string; nameKey: StringKey; list: string[] }> = [
  { key: 'caras', icon: '😊', nameKey: 'f5r6b.tray.cat.caras', list: ['😀', '😃', '😄', '😁', '😅', '😂', '🙂', '😉', '😊', '😍', '😘', '😜', '🤪', '🤨', '😐', '😴', '😌', '😔', '😟', '😢', '😭', '😤', '😠', '🤯', '😳', '🥺', '😬', '🙄', '🤔', '🤗', '🤫', '😷', '🤒', '🥳', '😎', '🤓', '🫠', '🫡', '🥹', '😮', '😱', '🤞', '🙏', '🤝', '💪', '👀', '🧠', '💬'] },
  { key: 'manos', icon: '👍', nameKey: 'f5r6b.tray.cat.manos', list: ['👍', '👎', '👏', '🙌', '👌', '✌️', '🤙', '🫶', '🤲', '☝️', '👉', '👈', '👆', '👇', '✋', '🖐️', '🫱', '🫲', '💅', '🦾', '👊', '✊', '🤛', '🤜'] },
  { key: 'trabajo', icon: '📋', nameKey: 'f5r6b.tray.cat.trabajo', list: ['📋', '📝', '📄', '📁', '🗂️', '📌', '📎', '🔗', '📞', '☎️', '📱', '💻', '🖥️', '⌨️', '🖨️', '📊', '📈', '📉', '🗓️', '⏰', '⏳', '🔔', '🔕', '🔍', '🔒', '🔑', '✅', '❌', '⚠️', '❗', '❓', '💡'] },
  { key: 'salud', icon: '🏥', nameKey: 'f5r6b.tray.cat.salud', list: ['🏥', '🩺', '💊', '🩹', '🚑', '🏠', '🏡', '🛏️', '🍎', '🥗', '🍞', '🥛', '🚌', '🚕', '🚗', '⚡', '💧', '🔥', '❄️', '👨‍👩‍👧', '👶', '🧑‍🦳', '♿', '🐕'] },
  { key: 'animo', icon: '🎉', nameKey: 'f5r6b.tray.cat.animo', list: ['🎉', '🎊', '🏆', '🥇', '⭐', '🌟', '✨', '💫', '🔥', '💯', '🚀', '🎯', '📣', '🙌', '❤️', '🧡', '💚', '💙', '💜', '🤍', '💖', '☕', '🍀', '🌈'] },
  { key: 'tiempo', icon: '🌤️', nameKey: 'f5r6b.tray.cat.tiempo', list: ['🌤️', '☀️', '🌥️', '🌧️', '⛈️', '❄️', '🌙', '🌃', '🌇', '🏙️', '🗽', '🌊', '🌳', '🍂', '🌸', '🕐', '🕕', '🕘', '📍', '🗺️'] },
];

const RECENT_EMOJI_KEY = 'pathway.chat.recentEmojis';
const RECENT_EMOJI_MAX = 8;

function loadRecentEmojis(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_EMOJI_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((e: unknown) => typeof e === 'string').slice(0, RECENT_EMOJI_MAX) : [];
  } catch {
    return [];
  }
}

function saveRecentEmoji(e: string): void {
  try {
    const prev = loadRecentEmojis().filter((x) => x !== e);
    localStorage.setItem(RECENT_EMOJI_KEY, JSON.stringify([e, ...prev].slice(0, RECENT_EMOJI_MAX)));
  } catch {
    // localStorage unavailable — degrade silently
  }
}

// ===== ChatButton (contrato conservado: lo montan los cinco shells) =====

export function ChatButton(): JSX.Element {
  const t = useT();
  const { setOpen } = useChatUi();
  const unread = useUnreadCount();
  const count = unread.data?.count ?? 0;
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="relative inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-sm text-nav-slate hover:text-nav-tealDark hover:bg-nav-tealTint transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
      aria-label={t('f5r6b.openChat')}
      title={t('f5r6b.list.title')}
    >
      <MessageSquare className="h-4 w-4" />
      <span className="sr-only sm:not-sr-only sm:text-xs">{t('f5r6b.list.title')}</span>
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-nav-teal text-white text-[10px] font-head font-bold tabular-nums">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

// ===== Overlay =====

export function ChatPanel(): JSX.Element | null {
  const t = useT();
  const me = useAuthStore((s) => s.user);
  const { open, setOpen, selectedThreadId, selectThread, prefill, clearPrefill } = useChatUi();
  const [composing, setComposing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const ctxSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && prefill && (prefill.composeImmediately ?? true)) setComposing(true);
  }, [open, prefill]);

  useBodyScrollLock(open);
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        // Una capa interna abierta (bandeja de emoji, mención, escalado) se
        // cierra ella primero — su propio listener la maneja.
        if (dialogRef.current?.querySelector('[data-chat-layer]')) return;
        if (composing) setComposing(false);
        else setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, composing, setOpen]);

  // Datos compartidos por las tres columnas.
  const threads = useChatThreads();
  const recipients = useChatRecipients();
  const presence = usePresenceMap(open);
  const messagesQ = useChatMessages(open ? selectedThreadId : null);

  const names = useMemo(() => {
    const m = new Map<string, { name: string; role: string }>();
    for (const r of recipients.data ?? []) m.set(String(r.id), { name: r.full_name, role: r.role });
    for (const mm of messagesQ.data?.members ?? []) m.set(String(mm.id), { name: mm.full_name, role: mm.role });
    return m;
  }, [recipients.data, messagesQ.data?.members]);

  if (!open || !me) return null;

  const selectedThread = (threads.data ?? []).find((x) => String(x.id) === String(selectedThreadId)) ?? null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="chat-panel-title" className="fixed inset-0 z-50 animate-fade-in">
      <div className="absolute inset-0 bg-nav-navy/40" onClick={() => setOpen(false)} aria-hidden />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="absolute inset-2 sm:inset-4 lg:inset-6 mx-auto max-w-[1400px] bg-white rounded-[14px] border-[1.5px] border-nav-card shadow-[0_8px_24px_rgba(18,43,68,.08)] flex flex-col overflow-hidden"
      >
        {/* Barra superior: título + Mi estado + cerrar */}
        <div className="flex-none flex items-center gap-3 flex-wrap px-4 sm:px-5 py-3 border-b-[1.5px] border-nav-card bg-white">
          <h2 id="chat-panel-title" className="font-head text-[17px] font-extrabold tracking-tight text-nav-navy">
            {t('f5r6b.title')}
          </h2>
          <div className="flex-1" />
          <PresencePills meId={String(me.id)} presence={presence.data ?? {}} />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            aria-label={t('f5r6b.close')}
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Cuerpo: tres columnas (xl) · dos + contexto abajo (md) · una (móvil) */}
        <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden">
          <div className="min-h-full xl:h-full grid grid-cols-1 md:grid-cols-[320px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(440px,1fr)_300px]">
            <div
              className={cn(
                'min-w-0 md:h-full md:min-h-0 md:overflow-hidden flex-col border-b-[1.5px] md:border-b-0 md:border-r-[1.5px] border-nav-card bg-nav-softer',
                selectedThreadId || composing ? 'hidden md:flex' : 'flex',
              )}
            >
              <ListPane
                me={me}
                threads={threads}
                names={names}
                presence={presence.data ?? {}}
                selectedThreadId={selectedThreadId}
                onSelect={(id) => {
                  setComposing(false);
                  selectThread(id);
                }}
                onCompose={() => setComposing(true)}
              />
            </div>

            <div
              className={cn(
                'min-w-0 md:h-full md:min-h-0 flex flex-col bg-nav-soft',
                !selectedThreadId && !composing ? 'hidden md:flex' : 'flex',
              )}
            >
              {composing ? (
                <NewThreadView
                  prefill={prefill}
                  onCancel={() => {
                    setComposing(false);
                    clearPrefill();
                  }}
                  onCreated={(id) => {
                    setComposing(false);
                    clearPrefill();
                    selectThread(id);
                  }}
                />
              ) : selectedThreadId ? (
                <ThreadView
                  key={selectedThreadId}
                  threadId={selectedThreadId}
                  thread={selectedThread}
                  messagesQ={messagesQ}
                  me={me}
                  names={names}
                  presence={presence.data ?? {}}
                  onBack={() => selectThread(null)}
                  onWantLink={() => ctxSearchRef.current?.focus()}
                  onClosePanel={() => setOpen(false)}
                />
              ) : (threads.data ?? []).length === 0 && !threads.isLoading ? (
                <div className="flex-1 flex items-center justify-center p-6">
                  <EmptyState
                    icon={<MessageSquare className="h-5 w-5" />}
                    title={t('f5r6b.empty.title')}
                    body={t('f5r6b.empty.body')}
                    action={{ label: t('f5r6b.empty.cta'), onClick: () => setComposing(true) }}
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center p-6">
                  <EmptyState
                    icon={<MessageSquare className="h-5 w-5" />}
                    title={t('f5r6b.pick.title')}
                    body={t('f5r6b.pick.body')}
                  />
                </div>
              )}
            </div>

            <div className="hidden md:block min-w-0 md:col-span-2 xl:col-span-1 border-t-[1.5px] xl:border-t-0 xl:border-l-[1.5px] border-nav-card bg-nav-softer xl:h-full xl:min-h-0 xl:overflow-y-auto">
              {selectedThreadId && !composing ? (
                <ContextPane
                  thread={selectedThread}
                  payload={messagesQ.data}
                  me={me}
                  names={names}
                  presence={presence.data ?? {}}
                  recipients={recipients.data ?? []}
                  ctxSearchRef={ctxSearchRef}
                  onClosePanel={() => setOpen(false)}
                />
              ) : (
                <div className="p-[18px]">
                  <p className="m-0 font-head text-[9.5px] font-bold tracking-[.14em] uppercase text-nav-faint">
                    {t('f5r6b.ctx.isCase')}
                  </p>
                  <p className="mt-3 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r6b.ctx.linkBody')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Mi estado =====

function PresencePills({ meId, presence }: { meId: string; presence: Record<string, PresenceState> }): JSX.Element {
  const t = useT();
  const setPresence = useSetPresence();
  const current: PresenceState = presence[meId] ?? 'free';
  const opts: Array<{ k: PresenceState; label: string }> = [
    { k: 'free', label: t('f5r6b.presence.free') },
    { k: 'call', label: t('f5r6b.presence.call') },
    { k: 'off', label: t('f5r6b.presence.off') },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-slate mr-1">
        {t('f5r6b.myStatus')}
      </span>
      {opts.map((o) => {
        const on = current === o.k;
        return (
          <button
            key={o.k}
            type="button"
            onClick={() => setPresence.mutate(o.k)}
            aria-pressed={on}
            className={cn(
              // pillBase del dibujo: 44px de alto, radio 8, Montserrat 12.5 bold.
              'min-h-[44px] px-4 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
              on
                ? 'bg-nav-navy text-white border-nav-navy hover:bg-nav-navyHover hover:border-nav-navyHover'
                : 'bg-white text-nav-navy border-nav-line hover:bg-[#f4fafa] hover:text-nav-tealDark hover:border-nav-teal',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ===== Columna izquierda =====

function ListPane({
  me,
  threads,
  names,
  presence,
  selectedThreadId,
  onSelect,
  onCompose,
}: {
  me: { id: string; fullName: string };
  threads: ReturnType<typeof useChatThreads>;
  names: Map<string, { name: string; role: string }>;
  presence: Record<string, PresenceState>;
  selectedThreadId: string | null;
  onSelect: (id: string) => void;
  onCompose: () => void;
}): JSX.Element {
  const t = useT();
  const lang = useLang();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<'todo' | 'casos' | 'no'>('todo');
  const muted = useChatMutes((s) => s.muted);
  const listRef = useRef<HTMLDivElement>(null);

  const rows = useMemo(() => {
    const all = (threads.data ?? []).map((th) => {
      const d = threadDisplay(th, String(me.id), names);
      const needsReply =
        th.unread > 0 && !muted[String(th.id)] && (d.tag === 'esc' || mentionsMe(th.last_message_body, me.fullName));
      return { th, d, needsReply };
    });
    const ql = q.trim().toLowerCase();
    if (!ql) return all;
    return all.filter(
      ({ th, d }) =>
        d.name.toLowerCase().includes(ql) ||
        th.subject.toLowerCase().includes(ql) ||
        (th.last_message_body ?? '').toLowerCase().includes(ql) ||
        (th.last_sender ?? '').toLowerCase().includes(ql),
    );
  }, [threads.data, names, me.id, me.fullName, muted, q]);

  const nCases = rows.filter(({ d }) => d.isCase).length;
  const nUnread = rows.filter(({ th }) => th.unread > 0 && !muted[String(th.id)]).length;

  const filtered = rows.filter(({ d, th }) => {
    if (filter === 'casos') return d.isCase;
    if (filter === 'no') return th.unread > 0 && !muted[String(th.id)];
    return true;
  });

  const groups: Array<{ key: string; title: string; amber?: boolean; items: typeof filtered }> = [
    { key: 'needs', title: t('f5r6b.group.needsReply'), amber: true, items: filtered.filter((r) => r.needsReply) },
    { key: 'cases', title: t('f5r6b.group.cases'), items: filtered.filter((r) => !r.needsReply && r.d.isCase) },
    { key: 'team', title: t('f5r6b.group.team'), items: filtered.filter((r) => !r.needsReply && !r.d.isCase) },
  ].filter((g) => g.items.length > 0);

  // Flechas: recorrer las filas de conversación (accesibilidad del dibujo).
  const onListKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const btns = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-thread-row]') ?? []);
    const idx = btns.indexOf(document.activeElement as HTMLButtonElement);
    if (idx === -1) return;
    e.preventDefault();
    const next = btns[Math.max(0, Math.min(btns.length - 1, idx + (e.key === 'ArrowDown' ? 1 : -1)))];
    next?.focus();
  };

  const filters: Array<{ k: 'todo' | 'casos' | 'no'; label: string; count: number | null }> = [
    { k: 'todo', label: t('f5r6b.filter.all'), count: null },
    { k: 'casos', label: t('f5r6b.filter.cases'), count: nCases },
    { k: 'no', label: t('f5r6b.filter.unread'), count: nUnread },
  ];

  return (
    <>
      <div className="flex-none px-[18px] pt-[18px] pb-3.5">
        <div className="flex items-center justify-between gap-3 mb-3.5">
          <p className="m-0 font-head text-[17px] font-extrabold tracking-tight text-nav-navy">{t('f5r6b.list.title')}</p>
          <button
            type="button"
            onClick={onCompose}
            className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            aria-label={t('f5r6b.list.new')}
            title={t('f5r6b.list.new')}
          >
            <Plus className="h-[18px] w-[18px]" />
          </button>
        </div>

        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('f5r6b.list.search')}
          aria-label={t('f5r6b.list.searchAria')}
          className="w-full box-border h-11 px-[13px] border-[1.5px] border-nav-card rounded-lg font-body text-[13px] text-nav-navy bg-white placeholder:text-nav-faint focus-visible:outline-none focus-visible:border-nav-teal"
        />

        <div className="flex gap-1.5 flex-wrap mt-3">
          {filters.map((f) => {
            const on = filter === f.k;
            return (
              <button
                key={f.k}
                type="button"
                onClick={() => setFilter(f.k)}
                aria-pressed={on}
                className={cn(
                  // chipBase del dibujo: 44px, cápsula redonda, borde 1.5.
                  'inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-full border-[1.5px] font-head text-[11.5px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                  on
                    ? 'bg-nav-navy text-white border-nav-navy'
                    : 'bg-white text-nav-slate border-nav-card hover:bg-[#f4fafa] hover:text-nav-tealDark hover:border-nav-teal',
                )}
              >
                {f.label}
                {f.count !== null && f.count > 0 && (
                  <span className={cn('tabular-nums font-bold', on ? 'text-white/75' : 'text-nav-faint')}>{f.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div ref={listRef} onKeyDown={onListKeyDown} className="flex-1 min-h-0 overflow-y-auto pb-3.5" role="list">
        {threads.isLoading && (
          <div className="px-[18px] space-y-2 pt-2">
            <Skeleton className="h-[72px] rounded-xl" />
            <Skeleton className="h-[72px] rounded-xl" />
            <Skeleton className="h-[72px] rounded-xl" />
          </div>
        )}
        {threads.error != null && (
          <div className="p-4">
            <ErrorState
              icon={<TriangleAlert className="h-5 w-5" />}
              title={t('f5r6b.err.title')}
              body={t('f5r6b.err.body')}
              action={{ label: t('f5r6b.err.retry'), onClick: () => void threads.refetch() }}
            />
          </div>
        )}
        {groups.map((g) => (
          <div key={g.key}>
            <p
              className={cn(
                'mx-[18px] mt-3.5 mb-2 font-head text-[9.5px] font-bold tracking-[.14em] uppercase',
                g.amber ? 'text-nav-amberText' : 'text-nav-faint',
              )}
            >
              {g.title}
            </p>
            {g.items.map(({ th, d }) => {
              const on = String(selectedThreadId) === String(th.id);
              const isMuted = !!muted[String(th.id)];
              const pres = d.otherId ? presence[d.otherId] : undefined;
              const unreadShown = th.unread > 0 && !isMuted;
              return (
                <button
                  key={th.id}
                  type="button"
                  data-thread-row
                  onClick={() => onSelect(String(th.id))}
                  aria-current={on ? 'true' : undefined}
                  className={cn(
                    'flex items-center gap-[11px] w-full box-border min-h-[72px] px-[18px] py-2.5 border-0 border-l-[3px] text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nav-teal',
                    on ? 'border-l-nav-teal bg-nav-tealTint hover:bg-[#d3e8e9]' : 'border-l-transparent bg-transparent hover:bg-[#f4fafa]',
                  )}
                >
                  <span className="relative flex-none">
                    <span
                      className={cn(
                        'inline-flex items-center justify-center w-[38px] h-[38px] rounded-full font-head text-[12.5px] font-bold',
                        d.tag === 'case' ? 'bg-nav-tealTint text-nav-tealDark' : d.tag === 'group' ? 'bg-nav-hair text-nav-tealDark' : 'bg-nav-navy text-white',
                      )}
                    >
                      {d.initials}
                    </span>
                    {pres && (
                      <span
                        className={cn('absolute -right-px -bottom-px w-2.5 h-2.5 rounded-full border-2 border-white', PRESENCE_DOT[pres])}
                        aria-hidden
                      />
                    )}
                  </span>
                  <span className="flex-1 min-w-0 text-left">
                    <span className="flex items-baseline gap-2">
                      <span
                        className={cn(
                          'font-head text-[13.5px] text-nav-navy whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px]',
                          unreadShown ? 'font-extrabold' : 'font-bold',
                        )}
                      >
                        {d.name}
                      </span>
                      {isMuted && <BellOff className="h-3 w-3 flex-none text-nav-faint" aria-label={t('f5r6b.tag.muted')} />}
                      <span className="flex-1" />
                      <span className="flex-none font-body text-[11px] text-nav-faint tabular-nums">
                        {th.last_message_at ? nyTime(th.last_message_at, lang) : ''}
                      </span>
                    </span>
                    <span className="flex items-center gap-2 mt-1">
                      <span
                        className={cn(
                          'flex-1 min-w-0 font-body text-[12px] whitespace-nowrap overflow-hidden text-ellipsis',
                          unreadShown ? 'text-nav-navy font-semibold' : 'text-nav-slate',
                        )}
                      >
                        {th.last_sender ? `${th.last_sender}: ` : ''}
                        {th.last_message_body ?? ''}
                      </span>
                      {unreadShown && (
                        <span className="flex-none inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-nav-teal text-white font-head text-[11px] font-extrabold tabular-nums">
                          {th.unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </>
  );
}

// ===== Hilo (columna central) =====

type StreamItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'system'; key: string; text: string }
  | { kind: 'msg'; key: string; m: ChatMessage; grouped: boolean };

function ThreadView({
  threadId,
  thread,
  messagesQ,
  me,
  names,
  presence,
  onBack,
  onWantLink,
  onClosePanel,
}: {
  threadId: string;
  thread: ThreadRow | null;
  messagesQ: ReturnType<typeof useChatMessages>;
  me: { id: string; fullName: string; role: string };
  names: Map<string, { name: string; role: string }>;
  presence: Record<string, PresenceState>;
  onBack: () => void;
  onWantLink: () => void;
  onClosePanel: () => void;
}): JSX.Element {
  const t = useT();
  const lang = useLang();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const send = useSendMessage(threadId);
  const react = useReact(threadId);
  const muted = useChatMutes((s) => s.muted);
  const toggleMute = useChatMutes((s) => s.toggleMute);
  const [draft, setDraft] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiCat, setEmojiCat] = useState('caras');
  const [recent, setRecent] = useState<string[]>(() => loadRecentEmojis());
  const [mentionOpen, setMentionOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const trayRef = useRef<HTMLDivElement>(null);

  const payload: ThreadMessagesPayload | undefined = messagesQ.data;
  const isMuted = !!muted[String(threadId)];
  const myPresence: PresenceState = presence[String(me.id)] ?? 'free';
  const d = thread ? threadDisplay(thread, String(me.id), names) : null;

  // El foco entra al compositor al elegir un hilo (accesibilidad del dibujo).
  useEffect(() => {
    textareaRef.current?.focus();
  }, [threadId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [payload?.messages.length]);

  useEffect(() => {
    if (!emojiOpen) return;
    const onClick = (e: MouseEvent): void => {
      if (trayRef.current && !trayRef.current.contains(e.target as Node)) setEmojiOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [emojiOpen]);

  useEffect(() => {
    if (!emojiOpen && !mentionOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setEmojiOpen(false);
        setMentionOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [emojiOpen, mentionOpen]);

  const insertAtCursor = (text: string): void => {
    const ta = textareaRef.current;
    if (!ta) {
      setDraft((prev) => prev + text);
      return;
    }
    const start = ta.selectionStart ?? draft.length;
    const end = ta.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + text + draft.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = start + text.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  const insertEmoji = (emoji: string): void => {
    insertAtCursor(emoji);
    saveRecentEmoji(emoji);
    setRecent(loadRecentEmojis());
  };

  const handleSend = (): void => {
    if (!draft.trim() || send.isPending) return;
    send.mutate(draft.trim(), { onSuccess: () => setDraft('') });
  };

  // Stream: mensajes + líneas de sistema (incorporaciones) + separadores de día.
  const stream: StreamItem[] = useMemo(() => {
    const events: Array<{ at: string; item: StreamItem }> = [];
    for (const m of payload?.messages ?? []) {
      events.push({ at: m.sent_at, item: { kind: 'msg', m, grouped: false, key: `m-${m.id}` } });
    }
    for (const j of payload?.joins ?? []) {
      events.push({
        at: j.joined_at,
        item: {
          kind: 'system',
          key: `j-${j.user_id}`,
          text: `${t('f5r6b.sys.joined', { name: j.full_name, by: j.added_by_name ?? '' })} · ${nyTime(j.joined_at, lang)}`,
        },
      });
    }
    events.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

    const todayKey = nyDateKey(new Date().toISOString());
    const yesterdayKey = nyDateKey(new Date(Date.now() - 86400000).toISOString());
    const out: StreamItem[] = [];
    let lastDay = '';
    let lastSender: string | null = null;
    for (const ev of events) {
      const day = nyDateKey(ev.at);
      if (day !== lastDay) {
        lastDay = day;
        lastSender = null;
        const label =
          day === todayKey
            ? t('f5r6b.day.today')
            : day === yesterdayKey
              ? t('f5r6b.day.yesterday')
              : new Intl.DateTimeFormat(lang === 'es' ? 'es' : 'en', { day: 'numeric', month: 'short', timeZone: NY }).format(
                  new Date(ev.at),
                );
        out.push({ kind: 'day', key: `d-${day}`, label });
      }
      if (ev.item.kind === 'msg') {
        const grouped = lastSender === String(ev.item.m.sender_id);
        lastSender = String(ev.item.m.sender_id);
        out.push({ ...ev.item, grouped });
      } else {
        lastSender = null;
        out.push(ev.item);
      }
    }
    return out;
  }, [payload?.messages, payload?.joins, t, lang]);

  const reactionsByMsg = useMemo(() => {
    const m = new Map<string, ThreadReaction[]>();
    for (const r of payload?.reactions ?? []) {
      const arr = m.get(String(r.message_id)) ?? [];
      arr.push(r);
      m.set(String(r.message_id), arr);
    }
    return m;
  }, [payload?.reactions]);

  // «Ver el caso vinculado» — resoluble según el rol (sin ruta no hay botón).
  const caseNav = useMemo(() => {
    const clientId = thread?.related_client_id;
    if (!clientId) return null;
    if (me.role === 'agent' || me.role === 'supervisor' || me.role === 'admin') {
      return () => {
        onClosePanel();
        navigate(`/clients/${clientId}`);
      };
    }
    return null; // navigator resuelve en ContextPane (por caseId); uploader sin ruta de perfil.
  }, [thread?.related_client_id, me.role, navigate, onClosePanel]);

  const subParts: string[] = [];
  if (d && !d.isGroup && d.otherId) {
    const o = names.get(d.otherId);
    if (o) subParts.push(t(roleKey(o.role)));
    const p = d.otherId ? presence[d.otherId] : undefined;
    if (p) subParts.push(t(p === 'free' ? 'f5r6b.presence.free' : p === 'call' ? 'f5r6b.presence.call' : 'f5r6b.presence.off'));
  }
  if (payload?.members?.length) subParts.push(t('f5r6b.thread.people', { n: payload.members.length }));
  if (payload?.members?.some((mm) => !mm.active)) subParts.push(t('f5r6b.people.deactivated'));

  return (
    <>
      {/* Encabezado del hilo */}
      <div className="flex-none flex items-center gap-3 min-h-[76px] px-4 sm:px-5 py-3.5 bg-white border-b-[1.5px] border-nav-card">
        <button
          type="button"
          onClick={onBack}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-nav-slate hover:bg-nav-hair"
          aria-label={t('f5r6b.list.title')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span
          className={cn(
            'flex-none inline-flex items-center justify-center w-10 h-10 rounded-full font-head text-[13px] font-bold',
            d?.tag === 'case' || d?.tag === 'group' ? 'bg-nav-tealTint text-nav-tealDark' : 'bg-nav-navy text-white',
          )}
        >
          {d?.initials ?? '·'}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-[9px] flex-wrap">
            <p className="m-0 font-head text-[16px] font-extrabold tracking-tight text-nav-navy truncate">{d?.name ?? ''}</p>
            {d?.tag && (
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 border font-head text-[10.5px] font-bold whitespace-nowrap',
                  d.tag === 'esc'
                    ? 'bg-nav-amberTint text-nav-amberText border-[#e6cf9a]'
                    : 'bg-nav-tealTint text-nav-tealDark border-[#c9e2e2]',
                )}
              >
                {d.tag === 'esc' ? t('f5r6b.tag.escalated') : d.tag === 'group' ? t('f5r6b.tag.group') : t('f5r6b.tag.case')}
              </span>
            )}
          </div>
          {subParts.length > 0 && <p className="mt-1 mb-0 text-[12.5px] text-nav-slate truncate">{subParts.join(' · ')}</p>}
        </div>
        <button
          type="button"
          onClick={() => toggleMute(String(threadId))}
          className={cn(
            'flex-none inline-flex items-center justify-center w-11 h-11 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
            isMuted ? 'text-nav-tealDark bg-nav-tealTint' : 'text-nav-slate hover:bg-nav-hair hover:text-nav-navy',
          )}
          aria-pressed={isMuted}
          aria-label={isMuted ? t('f5r6b.thread.unmute') : t('f5r6b.thread.mute')}
          title={isMuted ? t('f5r6b.thread.unmute') : t('f5r6b.thread.mute')}
        >
          {isMuted ? <Bell className="h-[18px] w-[18px]" /> : <BellOff className="h-[18px] w-[18px]" />}
        </button>
        {caseNav && (
          <button
            type="button"
            onClick={caseNav}
            className="flex-none inline-flex items-center justify-center w-11 h-11 rounded-lg text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            aria-label={t('f5r6b.thread.viewCase')}
            title={t('f5r6b.thread.viewCase')}
          >
            <ExternalLink className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>

      {/* Stream — lo único que scrollea */}
      <div ref={scrollRef} className="flex-1 min-h-[220px] overflow-y-auto px-4 sm:px-[22px] py-4" aria-live="polite">
        {messagesQ.isLoading && (
          <div className="space-y-3 pt-2">
            <Skeleton className="h-12 w-2/3 rounded-xl" />
            <Skeleton className="h-12 w-1/2 rounded-xl ml-auto" />
            <Skeleton className="h-12 w-3/5 rounded-xl" />
          </div>
        )}
        {!messagesQ.isLoading && stream.length === 0 && (
          <div className="flex justify-center pt-6">
            <span className="inline-flex items-start gap-2 max-w-[420px] bg-white border-[1.5px] border-nav-card rounded-[10px] px-3.5 py-3 font-body text-[11.5px] leading-relaxed text-nav-slate">
              <Lock className="h-3.5 w-3.5 flex-none mt-0.5" aria-hidden />
              {(payload?.members?.length ?? 2) <= 2 ? t('f5r6b.privacy.two') : t('f5r6b.privacy.many')}
            </span>
          </div>
        )}
        {stream.map((item, i) => {
          if (item.kind === 'day') {
            return (
              <div key={item.key} className="flex justify-center pt-1.5 pb-3">
                <span className="bg-nav-hair text-nav-slate rounded-full px-3.5 py-1 font-head text-[11px] font-bold">
                  {item.label}
                </span>
              </div>
            );
          }
          if (item.kind === 'system') {
            return (
              <div key={item.key} className="flex justify-center py-2.5">
                <span className="inline-flex items-center gap-2 bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] rounded-full px-3 py-1.5 font-body text-[12px] font-semibold">
                  <Info className="h-3.5 w-3.5 flex-none" aria-hidden />
                  {item.text}
                </span>
              </div>
            );
          }
          const m = item.m;
          const mine = String(m.sender_id) === String(me.id);
          const rx = reactionsByMsg.get(String(m.id)) ?? [];
          const seenBy = mine
            ? (payload?.reads ?? []).filter((r) => new Date(r.last_read_at).getTime() >= new Date(m.sent_at).getTime())
            : [];
          const seen = seenBy.length > 0;
          const seenAt = seen
            ? nyTime(seenBy.reduce((min, r) => (r.last_read_at < min ? r.last_read_at : min), seenBy[0]!.last_read_at), lang)
            : null;
          const receiptTitle = seen
            ? seenBy.length === 1
              ? t('f5r6b.msg.seenTitle', { name: seenBy[0]!.full_name, time: seenAt ?? '' })
              : t('f5r6b.msg.seenManyTitle', { n: seenBy.length })
            : t('f5r6b.msg.sentTitle', { time: nyTime(m.sent_at, lang) });
          const lines = m.body.split('\n');
          const refLine = lines[0]?.startsWith('🔗 ') ? lines[0] : null;
          const bodyText = refLine ? lines.slice(1).join('\n') : m.body;
          return (
            <motion.div
              key={item.key}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut', delay: Math.min(i, 8) * 0.03 }}
              className={cn('group', item.grouped ? 'py-0.5' : 'pt-2 pb-0.5')}
            >
              <div className={cn('flex items-end gap-2.5', mine ? 'flex-row-reverse' : 'flex-row')}>
                {!item.grouped ? (
                  <span
                    className={cn(
                      'flex-none inline-flex items-center justify-center w-8 h-8 rounded-full font-head text-[11px] font-bold text-white',
                      mine ? 'bg-nav-teal' : 'bg-nav-navy',
                    )}
                  >
                    {initialsOf(m.sender_name)}
                  </span>
                ) : (
                  <span className="flex-none w-8" aria-hidden />
                )}
                <div className={cn('flex flex-col max-w-[82%] lg:max-w-[62%]', mine ? 'items-end' : 'items-start')}>
                  {!item.grouped && (
                    <span className="block mb-1 font-head text-[12px] font-extrabold text-nav-navy">
                      {mine ? t('f5r6b.msg.you') : m.sender_name}
                      <span className="ml-1.5 font-body text-[11px] font-medium text-nav-faint">
                        {m.sender_role ? t(roleKey(m.sender_role)) : ''}
                      </span>
                    </span>
                  )}
                  <span
                    className={cn(
                      'block px-3.5 py-[11px] font-body text-[13.5px] leading-relaxed whitespace-pre-wrap break-words',
                      mine
                        ? 'bg-nav-navy text-white rounded-[14px_4px_14px_14px]'
                        : 'bg-white text-nav-navy border-[1.5px] border-nav-card rounded-[4px_14px_14px_14px]',
                    )}
                  >
                    {refLine && (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 mb-2 rounded-lg px-2.5 py-1.5 font-head text-[11px] font-bold tabular-nums',
                          mine ? 'bg-white/15 text-[#c9e2e2]' : 'bg-nav-tealTint text-nav-tealDark',
                        )}
                      >
                        <Link2 className="h-3 w-3 flex-none" aria-hidden />
                        {refLine.slice(2).trim()}
                      </span>
                    )}
                    {refLine && bodyText && <br />}
                    {bodyText}
                  </span>

                  {rx.length > 0 && (
                    <span className={cn('flex gap-1.5 flex-wrap mt-[7px]', mine ? 'justify-end' : 'justify-start')}>
                      {rx.map((r) => (
                        <motion.button
                          key={r.emoji}
                          type="button"
                          onClick={() => react.mutate({ messageId: String(m.id), emoji: r.emoji })}
                          whileHover={reduceMotion ? undefined : { y: -2 }}
                          aria-label={t('f5r6b.msg.reactSum', { n: r.count, emoji: r.emoji })}
                          className={cn(
                            'inline-flex items-center gap-1 min-h-[30px] px-2.5 rounded-full border-[1.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                            r.mine ? 'bg-[#d3e8e9] border-nav-teal' : 'bg-nav-tealTint border-[#c9e2e2] hover:bg-[#d3e8e9] hover:border-nav-teal',
                          )}
                        >
                          <motion.span
                            initial={reduceMotion ? undefined : { scale: 0.4, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 18 }}
                            className="text-[14px] leading-none"
                          >
                            {r.emoji}
                          </motion.span>
                          <span className="font-head text-[11px] font-extrabold text-nav-tealDark tabular-nums">{r.count}</span>
                        </motion.button>
                      ))}
                    </span>
                  )}

                  <span className="flex items-center gap-2 mt-1 font-body text-[11px] text-nav-faint tabular-nums">
                    {nyTime(m.sent_at, lang)}
                    {mine && (
                      <motion.span
                        key={seen ? 'seen' : 'sent'}
                        initial={reduceMotion || !seen ? undefined : { scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 420, damping: 16 }}
                        className={cn(
                          'inline-flex items-center gap-1 font-head text-[10.5px] font-bold tabular-nums cursor-help',
                          // #3aa4de: el azul de recibo — la única excepción de paleta,
                          // declarada en el dibujo («el único azul del sistema»).
                          seen ? 'text-[#3aa4de]' : 'text-nav-faint',
                        )}
                        title={receiptTitle}
                      >
                        {seen ? (
                          <span className="inline-flex -space-x-1" aria-hidden>
                            <Check className="h-3 w-3" />
                            <Check className="h-3 w-3" />
                          </span>
                        ) : (
                          <Minus className="h-3 w-3" aria-hidden />
                        )}
                        {seen ? t('f5r6b.msg.seen', { time: seenAt ?? '' }) : t('f5r6b.msg.sent')}
                      </motion.span>
                    )}
                    <span className="inline-flex items-center gap-0.5 ml-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                      {QUICK_SET.map((qe) => (
                        <motion.button
                          key={qe}
                          type="button"
                          onClick={() => react.mutate({ messageId: String(m.id), emoji: qe })}
                          whileHover={reduceMotion ? undefined : { scale: 1.3, y: -2 }}
                          className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full text-[14px] leading-none hover:bg-nav-tealTint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                          aria-label={t('f5r6b.msg.quickReact', { emoji: qe })}
                        >
                          {qe}
                        </motion.button>
                      ))}
                    </span>
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Compositor */}
      <div className="flex-none bg-white border-t-[1.5px] border-nav-card px-4 sm:px-5 pt-3.5 pb-4">
        {myPresence === 'call' && (
          <div className="flex items-center gap-2.5 flex-wrap mb-3 bg-nav-tealTint border-[1.5px] border-[#c9e2e2] rounded-[10px] px-3.5 py-[11px] font-body text-[12.5px] leading-normal text-nav-tealDark">
            <CircleMinus className="h-4 w-4 flex-none" aria-hidden />
            <span className="flex-1 min-w-[200px]">
              <b>{t('f5r6b.dnd.title')}</b> {t('f5r6b.dnd.body')}
            </span>
          </div>
        )}

        <AnimatePresence>
          {emojiOpen && (
            <motion.div
              ref={trayRef}
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.34, 1.2, 0.64, 1] }}
              className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-4 py-3.5 mb-3"
              role="dialog"
              aria-label={t('f5r6b.composer.emoji')}
              data-chat-layer
            >
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {EMOJI_SETS.map((c) => {
                  const on = emojiCat === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setEmojiCat(c.key)}
                      aria-pressed={on}
                      aria-label={t(c.nameKey)}
                      className={cn(
                        'inline-flex items-center justify-center w-[38px] h-[38px] rounded-[10px] border-[1.5px] text-[17px] leading-none transition-all hover:-translate-y-0.5 hover:bg-nav-tealTint hover:border-nav-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                        on ? 'bg-nav-tealTint border-nav-teal' : 'bg-white border-nav-card',
                      )}
                    >
                      {c.icon}
                    </button>
                  );
                })}
                <span className="flex-1" />
                <span className="font-head text-[11px] font-bold text-nav-slate">
                  {t(EMOJI_SETS.find((c) => c.key === emojiCat)?.nameKey ?? 'f5r6b.tray.cat.caras')}
                </span>
              </div>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(38px,1fr))] gap-1 max-h-[172px] overflow-y-auto">
                {(EMOJI_SETS.find((c) => c.key === emojiCat)?.list ?? []).map((e, i) => (
                  <motion.button
                    key={`${e}-${i}`}
                    type="button"
                    onClick={() => insertEmoji(e)}
                    initial={reduceMotion ? undefined : { scale: 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 20, delay: Math.min(i, 24) * 0.012 }}
                    whileHover={reduceMotion ? undefined : { scale: 1.35, y: -3 }}
                    className="inline-flex items-center justify-center w-full h-[38px] rounded-[10px] text-[20px] leading-none hover:bg-nav-tealTint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                    aria-label={t('f5r6b.tray.insert', { emoji: e })}
                  >
                    {e}
                  </motion.button>
                ))}
              </div>
              {recent.length > 0 && (
                <div className="flex items-center gap-2.5 flex-wrap mt-3 pt-3 border-t border-nav-hair">
                  <span className="font-head text-[10px] font-bold tracking-[.1em] uppercase text-nav-faint">
                    {t('f5r6b.tray.recent')}
                  </span>
                  {recent.slice(0, 6).map((e) => (
                    <motion.button
                      key={e}
                      type="button"
                      onClick={() => insertEmoji(e)}
                      whileHover={reduceMotion ? undefined : { scale: 1.28, rotate: -6 }}
                      className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-[10px] bg-white text-[18px] leading-none hover:bg-nav-tealTint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                      aria-label={t('f5r6b.tray.insert', { emoji: e })}
                    >
                      {e}
                    </motion.button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-3.5 py-3">
          <div className="flex items-end gap-2.5">
            <textarea
              ref={textareaRef}
              rows={2}
              value={draft}
              maxLength={4000}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                // Enter envía · Shift+Enter baja de línea (el dibujo: Ctrl+Enter
                // es un atajo de correo, no de chat).
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={t('f5r6b.composer.placeholder')}
              aria-label={t('f5r6b.composer.aria')}
              className="flex-1 min-w-0 box-border px-3 py-[11px] border-[1.5px] border-nav-line rounded-[10px] font-body text-[13.5px] leading-relaxed text-nav-navy bg-white resize-none placeholder:text-nav-faint focus-visible:outline-none focus-visible:border-nav-teal"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!draft.trim() || send.isPending}
              aria-label={t('f5r6b.composer.send')}
              className={cn(
                'flex-none inline-flex items-center justify-center w-[52px] h-[52px] rounded-[10px] border-[1.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                !draft.trim() || send.isPending
                  ? 'bg-nav-line border-nav-line text-white cursor-not-allowed'
                  : 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover hover:border-nav-navyHover',
              )}
            >
              {send.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Send className="h-[18px] w-[18px]" />}
            </button>
          </div>
          {send.error != null && <p className="mt-2 mb-0 text-[12px] text-nav-red">{t('f5r6b.composer.sendFailed')}</p>}

          <div className="relative flex items-center gap-2 flex-wrap mt-3">
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              aria-pressed={emojiOpen}
              aria-expanded={emojiOpen}
              className={cn(
                'inline-flex items-center gap-[7px] min-h-[44px] px-3 rounded-lg border-[1.5px] font-head text-[12px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                emojiOpen
                  ? 'bg-nav-tealTint border-nav-teal text-nav-tealDark'
                  : 'bg-white border-nav-line text-nav-navy hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark',
              )}
            >
              <motion.span
                animate={emojiOpen && !reduceMotion ? { rotate: [0, -9, 9, 0] } : { rotate: 0 }}
                transition={{ duration: 0.5 }}
                className="text-[15px] leading-none"
              >
                😊
              </motion.span>
              {t('f5r6b.composer.emoji')}
            </button>
            <ComposerTool
              icon={<Link2 className="h-[15px] w-[15px]" aria-hidden />}
              label={t('f5r6b.composer.linkCase')}
              onClick={() => {
                if (thread?.related_client_id && d) {
                  insertAtCursor(t('f5r6b.caseRef.line', { name: d.name }) + '\n');
                } else {
                  onWantLink();
                }
              }}
            />
            <ComposerTool
              icon={<AtSign className="h-[15px] w-[15px]" aria-hidden />}
              label={t('f5r6b.composer.mention')}
              onClick={() => setMentionOpen((v) => !v)}
              pressed={mentionOpen}
            />
            <ComposerTool
              icon={<TriangleAlert className="h-[15px] w-[15px]" aria-hidden />}
              label={t('f5r6b.composer.escalate')}
              onClick={() => setEscalateOpen(true)}
            />
            <span className="flex-1" />
            <span className="font-body text-[11.5px] text-nav-faint">{t('f5r6b.composer.rule')}</span>

            {mentionOpen && (
              <div
                data-chat-layer
                className="absolute bottom-full left-0 mb-2 w-[260px] bg-white border-[1.5px] border-nav-card rounded-xl shadow-[0_8px_24px_rgba(18,43,68,.08)] p-2 z-10"
              >
                <p className="mx-2 my-1.5 font-head text-[10px] font-bold tracking-[.1em] uppercase text-nav-faint">
                  {t('f5r6b.mention.title')}
                </p>
                {(payload?.members ?? [])
                  .filter((mm) => String(mm.id) !== String(me.id))
                  .map((mm) => (
                    <button
                      key={mm.id}
                      type="button"
                      onClick={() => {
                        insertAtCursor(`@${mm.full_name} `);
                        setMentionOpen(false);
                      }}
                      className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-left font-body text-[13px] text-nav-navy hover:bg-nav-tealTint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
                    >
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-nav-navy text-white font-head text-[10px] font-bold">
                        {initialsOf(mm.full_name)}
                      </span>
                      <span className="truncate">{mm.full_name}</span>
                      <span className="ml-auto text-[10.5px] text-nav-faint">{t(roleKey(mm.role))}</span>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {escalateOpen && (
        <EscalateModal
          threadTitle={d?.name ?? ''}
          relatedClientId={thread?.related_client_id ?? null}
          onClose={() => setEscalateOpen(false)}
        />
      )}
    </>
  );
}

function ComposerTool({
  icon,
  label,
  onClick,
  pressed,
}: {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  pressed?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={cn(
        'inline-flex items-center gap-[7px] min-h-[44px] px-[13px] rounded-lg border-[1.5px] font-head text-[12px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
        pressed
          ? 'bg-nav-tealTint border-nav-teal text-nav-tealDark'
          : 'bg-white border-nav-line text-nav-navy hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

// ===== Escalado =====

function EscalateModal({
  threadTitle,
  relatedClientId,
  onClose,
}: {
  threadTitle: string;
  relatedClientId: string | null;
  onClose: () => void;
}): JSX.Element {
  const t = useT();
  const recipients = useChatRecipients();
  const create = useCreateThread();
  const { selectThread } = useChatUi();
  const [ownerId, setOwnerId] = useState('');
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const owners = (recipients.data ?? []).filter((r) => r.role === 'supervisor' || r.role === 'admin');

  const submit = (): void => {
    if (!ownerId || !note.trim() || create.isPending) return;
    const dueTxt = due ? ` · ${t('f5r6b.esc.msgDue', { date: due })}` : '';
    create.mutate(
      {
        subject: `${t('f5r6b.esc.subjectPrefix')} · ${threadTitle}`.slice(0, 200),
        participants: [ownerId],
        firstMessage: `${note.trim()}${dueTxt}`.slice(0, 4000),
        relatedClientId: relatedClientId ?? undefined,
      },
      {
        onSuccess: (r) => {
          onClose();
          selectThread(r.id);
        },
      },
    );
  };

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('f5r6b.esc.button')}
      data-chat-layer
    >
      <div className="absolute inset-0 bg-nav-navy/30 rounded-[14px]" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-[420px] bg-white border-[1.5px] border-nav-card rounded-[14px] shadow-[0_8px_24px_rgba(18,43,68,.16)] p-5">
        <p className="m-0 font-head text-[15px] font-extrabold text-nav-navy">{t('f5r6b.esc.button')}</p>
        <p className="mt-2 mb-4 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r6b.esc.body')}</p>

        <label className="block font-head text-[10.5px] font-bold tracking-[.1em] uppercase text-nav-slate mb-1.5">
          {t('f5r6b.esc.owner')}
        </label>
        <select
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
          className="w-full box-border h-11 px-3 mb-3.5 border-[1.5px] border-nav-line rounded-lg font-body text-[13px] text-nav-navy bg-white focus-visible:outline-none focus-visible:border-nav-teal"
        >
          <option value="">—</option>
          {owners.map((o) => (
            <option key={o.id} value={o.id}>
              {o.full_name} · {t(roleKey(o.role))}
            </option>
          ))}
        </select>

        <label className="block font-head text-[10.5px] font-bold tracking-[.1em] uppercase text-nav-slate mb-1.5">
          {t('f5r6b.esc.due')}
        </label>
        <input
          type="date"
          value={due}
          onChange={(e) => setDue(e.target.value)}
          className="w-full box-border h-11 px-3 mb-3.5 border-[1.5px] border-nav-line rounded-lg font-body text-[13px] text-nav-navy bg-white focus-visible:outline-none focus-visible:border-nav-teal"
        />

        <label className="block font-head text-[10.5px] font-bold tracking-[.1em] uppercase text-nav-slate mb-1.5">
          {t('f5r6b.esc.note')}
        </label>
        <textarea
          rows={3}
          value={note}
          maxLength={4000}
          onChange={(e) => setNote(e.target.value)}
          className="w-full box-border px-3 py-2.5 border-[1.5px] border-nav-line rounded-lg font-body text-[13px] leading-relaxed text-nav-navy bg-white resize-none focus-visible:outline-none focus-visible:border-nav-teal"
        />

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] px-4 rounded-lg font-head text-[12.5px] font-bold text-nav-slate hover:bg-nav-hair focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            {t('f5r6b.new.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ownerId || !note.trim() || create.isPending}
            className={cn(
              'inline-flex items-center gap-2 min-h-[44px] px-5 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
              !ownerId || !note.trim() || create.isPending
                ? 'bg-nav-line border-nav-line text-white cursor-not-allowed'
                : 'bg-white border-nav-amber text-nav-amberText hover:bg-nav-amberTint',
            )}
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('f5r6b.esc.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Columna derecha =====

interface PickerRow {
  clientId: string;
  assignmentId?: string;
  caseId?: string;
  name: string;
  meta: string;
}

function ContextPane({
  thread,
  payload,
  me,
  names,
  presence,
  recipients,
  ctxSearchRef,
  onClosePanel,
}: {
  thread: ThreadRow | null;
  payload: ThreadMessagesPayload | undefined;
  me: { id: string; fullName: string; role: string };
  names: Map<string, { name: string; role: string }>;
  presence: Record<string, PresenceState>;
  recipients: Recipient[];
  ctxSearchRef: React.RefObject<HTMLInputElement>;
  onClosePanel: () => void;
}): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const threadId = thread ? String(thread.id) : null;
  const linkCase = useLinkCase(threadId);
  const addParticipant = useAddParticipant(threadId);
  const [changing, setChanging] = useState(false);
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);

  const isLinked = !!thread?.related_client_id && !changing;
  const d = thread ? threadDisplay(thread, String(me.id), names) : null;
  const members = payload?.members ?? [];
  const role = me.role;

  // --- picker: adaptadores por rol (todo sobre endpoints VIVOS) ---
  const canGlobalSearch = role === 'agent' || role === 'supervisor' || role === 'admin';
  const qTrim = q.trim();

  const searchQ = useQuery({
    queryKey: ['chat', 'caseSearch', qTrim],
    queryFn: () => clientsApi.searchPatients(qTrim, 10),
    enabled: canGlobalSearch && !isLinked && qTrim.length >= 3 && !!thread,
    staleTime: 30_000,
  });

  // E35-D — CONTENCIÓN. Hasta acá, abrir cualquier hilo como navigator bajaba
  // la cartera ENTERA (`me/dashboard`): con ~58.000 casos en una persona son
  // decenas de MB, el mismo congelamiento que se arregló en «Hoy». Además leía
  // campos que la respuesta no trae (`client_id`, `first_name`…): los nombres
  // salían vacíos y el `clientId` del enlace también.
  //
  // Ahora pide la primera página (25) de la cartera en el orden de siempre
  // (`filter=cases`, sin PHI, con `clientId` real) y los nombres de ESAS filas
  // por `case-labels`. Alcance honesto: el selector ofrece y filtra esos 25
  // (los más urgentes). Buscar en TODA la cartera necesita que `case-search`
  // devuelva `clientId` y una búsqueda caso-por-cliente para «abrir el caso»:
  // anotado como pendiente en la entrega E35-D.
  const navPedido: HoyPedido = {
    pages: HOY_PAGINAS_INICIO, filter: 'cases', pFilter: 0, hoy: hoyLocal(),
  };
  const navCasesQ = useQuery({
    queryKey: ['chat', 'navCases'],
    queryFn: () => navigatorApi.hoy(navPedido),
    enabled: role === 'navigator' && !!thread,
    staleTime: 60_000,
  });
  const navItems = navCasesQ.data?.filter?.rows ?? [];
  const navNames = useCaseLabels(role === 'navigator' ? navItems.map((it) => it.id) : []);

  const upQueueQ = useQuery({
    queryKey: ['chat', 'upQueue'],
    queryFn: () => apiCall<{ items: Array<Record<string, unknown>> }>('/uploader/queue?limit=100'),
    enabled: role === 'uploader' && !isLinked && !!thread,
    staleTime: 60_000,
  });

  const pickerRows: PickerRow[] = useMemo(() => {
    if (canGlobalSearch) {
      return (searchQ.data ?? []).map((r) => ({
        clientId: String(r.id),
        name: `${r.firstName} ${r.lastName}`.trim(),
        meta: `CIN ****${r.cinLast6}`,
      }));
    }
    if (role === 'navigator') {
      const rows = navItems.map((it) => {
        const l = navNames.get(it.id);
        return {
          clientId: String(it.clientId ?? ''),
          caseId: String(it.id ?? ''),
          name: l ? `${l.firstName} ${l.lastName}`.trim() : '',
          meta: String(it.status ?? ''),
        };
      });
      const ql = qTrim.toLowerCase();
      return (ql ? rows.filter((r) => r.name.toLowerCase().includes(ql)) : rows).slice(0, ql ? 10 : 5);
    }
    if (role === 'uploader') {
      const items = upQueueQ.data?.items ?? [];
      const rows = items.map((it) => ({
        clientId: String(it.clientId ?? ''),
        name: String(it.clientLastName ?? ''),
        meta: `CIN ****${String(it.clientCinLast6 ?? '')}`,
      }));
      const ql = qTrim.toLowerCase();
      return (ql ? rows.filter((r) => (r.name + ' ' + r.meta).toLowerCase().includes(ql)) : rows).slice(0, ql ? 10 : 5);
    }
    return [];
  }, [canGlobalSearch, role, searchQ.data, navItems, navNames, upQueueQ.data, qTrim]);

  const scopeKey: StringKey =
    role === 'uploader'
      ? 'f5r6b.ctx.scope.uploader'
      : role === 'navigator'
        ? 'f5r6b.ctx.scope.navigator'
        : // agente/supervisor/admin: el endpoint vivo busca en TODO el programa —
          // el chip dice la verdad (el alcance por puesto es NECESITA-SERVER).
          'f5r6b.ctx.scope.admin';

  const doLink = (row: PickerRow): void => {
    linkCase.mutate(
      {
        clientId: row.clientId,
        assignmentId: row.assignmentId,
        title: `${t('f5r6b.tag.case')} · ${row.name}`.slice(0, 200),
      },
      {
        onSuccess: () => {
          setChanging(false);
          setQ('');
        },
      },
    );
  };

  const openCase = (): void => {
    const clientId = thread?.related_client_id;
    if (!clientId) return;
    if (canGlobalSearch) {
      onClosePanel();
      navigate(`/clients/${clientId}`);
      return;
    }
    if (role === 'navigator') {
      const item = navItems.find((it) => String(it.clientId) === String(clientId));
      if (item) {
        onClosePanel();
        navigate(`/navigator/cases/${String(item.id)}`);
      }
    }
  };
  const caseOpenable =
    !!thread?.related_client_id &&
    (canGlobalSearch ||
      (role === 'navigator' && navItems.some((it) => String(it.clientId) === String(thread?.related_client_id))));

  const addables = recipients.filter((r) => !members.some((mm) => String(mm.id) === String(r.id)));

  return (
    <div className="p-[18px] pb-[22px]">
      {isLinked && d ? (
        <>
          <div className="flex items-center justify-between gap-2.5 mb-3">
            <p className="m-0 font-head text-[9.5px] font-bold tracking-[.14em] uppercase text-nav-faint">{t('f5r6b.ctx.linked')}</p>
            <button
              type="button"
              onClick={() => setChanging(true)}
              className="min-h-[32px] px-2.5 rounded-lg font-head text-[11.5px] font-bold text-nav-teal hover:bg-nav-tealTint hover:text-nav-tealDark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            >
              {t('f5r6b.ctx.change')}
            </button>
          </div>
          <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-[18px] py-4 mb-[22px]">
            <p className="m-0 mb-1.5 font-head text-[15px] font-extrabold tracking-tight text-nav-navy">{thread!.subject}</p>
            <p className="m-0 mb-3 text-[12px] text-nav-slate tabular-nums">
              {t('f5r6b.thread.people', { n: members.length || thread!.participants.length })}
            </p>
            {caseOpenable && (
              <button
                type="button"
                onClick={openCase}
                className="inline-flex items-center justify-center gap-2 w-full box-border min-h-[44px] mt-1 bg-white text-nav-navy border-[1.5px] border-nav-line rounded-lg font-head text-[12.5px] font-bold hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              >
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {t('f5r6b.ctx.open')}
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="m-0 mb-3 font-head text-[9.5px] font-bold tracking-[.14em] uppercase text-nav-faint">{t('f5r6b.ctx.isCase')}</p>
          <div className="bg-white border-[1.5px] border-[#c9e2e2] rounded-xl px-[18px] py-4 mb-[22px]">
            <p className="m-0 mb-3 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r6b.ctx.linkBody')}</p>
            <input
              ref={ctxSearchRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('f5r6b.ctx.searchPlaceholder')}
              aria-label={t('f5r6b.ctx.searchAria')}
              className="w-full box-border h-11 px-3 border-[1.5px] border-nav-line rounded-lg font-body text-[13px] text-nav-navy bg-white placeholder:text-nav-faint focus-visible:outline-none focus-visible:border-nav-teal"
            />
            <div className="flex items-center gap-2 flex-wrap mt-[11px]">
              <span className="inline-flex items-center bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] rounded-full px-2.5 py-1 font-head text-[10.5px] font-bold">
                {t(scopeKey)}
              </span>
            </div>
          </div>

          <p className="m-0 mb-3 font-head text-[9.5px] font-bold tracking-[.14em] uppercase text-nav-faint">
            {qTrim.length === 0
              ? canGlobalSearch
                ? t('f5r6b.ctx.searchHint')
                : t('f5r6b.ctx.recent')
              : pickerRows.length === 1
                ? t('f5r6b.ctx.match', { n: 1 })
                : t('f5r6b.ctx.matches', { n: pickerRows.length })}
          </p>
          <div className="flex flex-col gap-2 mb-[22px]">
            {(searchQ.isFetching || navCasesQ.isFetching || upQueueQ.isFetching) && <Skeleton className="h-[56px] rounded-[10px]" />}
            {pickerRows.map((row, i) => (
              <button
                key={`${row.clientId}-${i}`}
                type="button"
                onClick={() => doLink(row)}
                disabled={linkCase.isPending}
                className="flex items-center gap-2.5 w-full box-border min-h-[56px] px-3 py-2 bg-white border-[1.5px] border-nav-card rounded-[10px] text-left hover:bg-[#f4fafa] hover:border-nav-teal hover:-translate-y-0.5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
              >
                <span className="flex-1 min-w-0">
                  <span className="block font-head text-[12.5px] font-bold text-nav-navy truncate">{row.name}</span>
                  <span className="block mt-0.5 text-[11.5px] text-nav-slate tabular-nums truncate">{row.meta}</span>
                </span>
              </button>
            ))}
            {qTrim.length >= 3 && !searchQ.isFetching && !navCasesQ.isFetching && !upQueueQ.isFetching && pickerRows.length === 0 && (
              <div className="bg-white border-[1.5px] border-nav-card rounded-[10px] px-4 py-3.5">
                <p className="m-0 text-[12.5px] leading-relaxed text-nav-slate">{t('f5r6b.ctx.noResults')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Personas */}
      <div className="flex items-center justify-between gap-2.5 mb-3">
        <p className="m-0 font-head text-[9.5px] font-bold tracking-[.14em] uppercase text-nav-faint">
          {t('f5r6b.people.title', { n: members.length || (thread?.participants.length ?? 0) })}
        </p>
        <button
          type="button"
          onClick={() => setAddOpen((v) => !v)}
          aria-expanded={addOpen}
          aria-label={t('f5r6b.people.addAria')}
          className="inline-flex items-center gap-1.5 min-h-[32px] px-2.5 bg-white border-[1.5px] border-nav-line rounded-lg font-head text-[11.5px] font-bold text-nav-navy hover:bg-[#f4fafa] hover:border-nav-teal hover:text-nav-tealDark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          {t('f5r6b.people.add')}
        </button>
      </div>

      {addOpen && (
        <div className="bg-white border-[1.5px] border-nav-card rounded-[10px] p-2 mb-3 max-h-[220px] overflow-y-auto">
          {addables.map((r) => (
            <button
              key={r.id}
              type="button"
              disabled={addParticipant.isPending}
              onClick={() =>
                addParticipant.mutate(String(r.id), {
                  onSuccess: () => setAddOpen(false),
                })
              }
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-left font-body text-[13px] text-nav-navy hover:bg-nav-tealTint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            >
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-nav-navy text-white font-head text-[10px] font-bold">
                {initialsOf(r.full_name)}
              </span>
              <span className="truncate">{r.full_name}</span>
              <span className="ml-auto text-[10.5px] text-nav-faint">{t(roleKey(r.role))}</span>
            </button>
          ))}
          {addParticipant.error != null && (
            <p className="mx-2 my-1.5 text-[11.5px] text-nav-red">{(addParticipant.error as Error).message}</p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 mb-3.5">
        {members.map((mm) => {
          const p = presence[String(mm.id)];
          return (
            <div key={mm.id} className="flex items-center gap-2.5 min-h-[48px] px-2.5 py-[7px] bg-white border-[1.5px] border-nav-card rounded-[10px]">
              <span className="flex-none inline-flex items-center justify-center w-[30px] h-[30px] rounded-full bg-nav-navy text-white font-head text-[11px] font-bold">
                {initialsOf(mm.full_name)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-head text-[12.5px] font-bold text-nav-navy truncate">
                  {String(mm.id) === String(me.id) ? t('f5r6b.msg.you') : mm.full_name}
                </span>
                <span className="block mt-0.5 text-[11.5px] text-nav-slate truncate">
                  {t(roleKey(mm.role))}
                  {!mm.active ? ` · ${t('f5r6b.people.deactivated')}` : ''}
                </span>
              </span>
              {p && <span className={cn('flex-none w-[9px] h-[9px] rounded-full', PRESENCE_DOT[p])} aria-hidden />}
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-[9px] bg-white border-[1.5px] border-nav-card rounded-[10px] px-3.5 py-[13px] font-body text-[11.5px] leading-relaxed text-nav-slate">
        <Lock className="h-[15px] w-[15px] flex-none mt-px" aria-hidden />
        <span>{members.length <= 2 ? t('f5r6b.privacy.two') : t('f5r6b.privacy.many')}</span>
      </div>

      <div className="h-[22px]" />

      <p className="m-0 mb-3 font-head text-[9.5px] font-bold tracking-[.14em] uppercase text-nav-faint">{t('f5r6b.esc.kicker')}</p>
      <div className="bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[18px] py-4">
        <p className="m-0 mb-2.5 text-[12.5px] leading-relaxed text-nav-amberText">{t('f5r6b.esc.body')}</p>
        <button
          type="button"
          onClick={() => setEscalateOpen(true)}
          className="inline-flex items-center justify-center w-full box-border min-h-[44px] bg-white text-nav-amberText border-[1.5px] border-nav-amber rounded-lg font-head text-[12.5px] font-bold hover:bg-white/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          {t('f5r6b.esc.button')}
        </button>
      </div>

      {escalateOpen && (
        <EscalateModal
          threadTitle={d?.name ?? ''}
          relatedClientId={thread?.related_client_id ?? null}
          onClose={() => setEscalateOpen(false)}
        />
      )}
    </div>
  );
}

// ===== Nueva conversación (el asunto obligatorio MURIÓ) =====

function friendlyCreateThreadError(raw: string): string {
  const m = raw || '';
  if (/participants must contain no more than/i.test(m)) {
    return 'Demasiados destinatarios — máximo 50 por conversación. Quitá algunos y reintentá.';
  }
  if (/participants must contain at least/i.test(m)) {
    return 'Seleccioná al menos un destinatario antes de iniciar la conversación.';
  }
  if (/firstMessage must be shorter than or equal to/i.test(m)) {
    return 'El mensaje inicial es demasiado largo — máximo 4000 caracteres.';
  }
  return m || 'No se pudo crear la conversación. Reintentá en unos segundos.';
}

function NewThreadView({
  prefill,
  onCancel,
  onCreated,
}: {
  prefill?: ChatPrefill | null;
  onCancel: () => void;
  onCreated: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const recipients = useChatRecipients();
  const create = useCreateThread();
  const [selected, setSelected] = useState<string[]>(prefill?.recipientId ? [prefill.recipientId] : []);
  const [firstMessage, setFirstMessage] = useState(prefill?.message ?? '');
  const [q, setQ] = useState('');

  // Prefill por rol (contrato vivo: MyUploadStatusPanel del agente).
  useEffect(() => {
    if (selected.length > 0) return;
    if (!prefill?.recipientRoles?.length) return;
    if (!recipients.data) return;
    const matching = recipients.data
      .filter((r: Recipient) => (prefill.recipientRoles as string[]).includes(r.role))
      .sort((a: Recipient, b: Recipient) => a.full_name.localeCompare(b.full_name));
    if (matching[0]) setSelected([matching[0].id]);
  }, [recipients.data, prefill, selected.length]);

  const PARTICIPANTS_CAP = 50;
  const toggle = (id: string): void => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id].slice(0, PARTICIPANTS_CAP)));
  };

  // Conserva T-7: selección en bloque por rol.
  const countsByRole = (recipients.data ?? []).reduce<Record<string, number>>((acc, r: Recipient) => {
    acc[r.role] = (acc[r.role] ?? 0) + 1;
    return acc;
  }, {});
  const selectAllOfRole = (role: string): void => {
    if (!recipients.data) return;
    const ids = recipients.data.filter((r: Recipient) => r.role === role).map((r) => r.id);
    setSelected((prev) => Array.from(new Set([...prev, ...ids])).slice(0, PARTICIPANTS_CAP));
  };

  const ql = q.trim().toLowerCase();
  const list = (recipients.data ?? []).filter((r) => !ql || r.full_name.toLowerCase().includes(ql));

  const submit = (): void => {
    if (selected.length === 0 || create.isPending) return;
    // El asunto se deriva: prefill > nombres elegidos > fallback. (El campo
    // obligatorio murió; el server sigue exigiendo subject y acá se cumple.)
    const namesTxt = (recipients.data ?? [])
      .filter((r) => selected.includes(r.id))
      .map((r) => r.full_name)
      .slice(0, 3)
      .join(', ');
    const subject = (prefill?.subject?.trim() || namesTxt || t('f5r6b.new.fallbackSubject')).slice(0, 200);
    create.mutate(
      {
        subject,
        participants: selected,
        firstMessage: firstMessage.trim() || undefined,
        relatedClientId: prefill?.relatedClientId,
        relatedAssignmentId: prefill?.relatedAssignmentId,
      },
      { onSuccess: (r) => onCreated(r.id) },
    );
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
      <div className="flex items-center justify-between gap-3 mb-3.5">
        <p className="m-0 font-head text-[16px] font-extrabold tracking-tight text-nav-navy">{t('f5r6b.new.title')}</p>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-[36px] px-3 rounded-lg font-head text-[12px] font-bold text-nav-slate hover:bg-nav-hair focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          {t('f5r6b.new.cancel')}
        </button>
      </div>

      <p className="m-0 mb-2 font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate">{t('f5r6b.new.who')}</p>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('f5r6b.new.filter')}
        aria-label={t('f5r6b.new.filter')}
        className="w-full box-border h-11 px-3 mb-2.5 border-[1.5px] border-nav-line rounded-lg font-body text-[13px] text-nav-navy bg-white placeholder:text-nav-faint focus-visible:outline-none focus-visible:border-nav-teal"
      />

      <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
        {(['agent', 'supervisor', 'uploader', 'admin', 'navigator'] as const).map((role) => {
          const n = countsByRole[role] ?? 0;
          if (n === 0) return null;
          return (
            <button
              key={role}
              type="button"
              onClick={() => selectAllOfRole(role)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-head text-[11px] font-bold bg-nav-tealTint text-nav-tealDark border border-[#c9e2e2] hover:bg-[#d3e8e9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
            >
              + {t(roleKey(role))} ({n})
            </button>
          );
        })}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={() => setSelected([])}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-head text-[11px] font-bold bg-nav-hair text-nav-slate border border-nav-card hover:bg-nav-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          >
            × {selected.length}
          </button>
        )}
        {selected.length >= PARTICIPANTS_CAP && (
          <span className="text-[10px] text-nav-amberText">{t('f5r6b.new.max', { n: PARTICIPANTS_CAP })}</span>
        )}
      </div>

      {recipients.isLoading && <Skeleton className="h-24 rounded-[10px]" />}
      <div className="flex flex-col gap-1.5 mb-4">
        {list.map((r) => {
          const on = selected.includes(r.id);
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => toggle(r.id)}
              aria-pressed={on}
              className={cn(
                'inline-flex items-center gap-2.5 px-3 py-2 rounded-[10px] border-[1.5px] text-left font-body text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                on ? 'bg-nav-tealTint border-nav-teal text-nav-navy' : 'bg-white border-nav-card text-nav-navy hover:bg-[#f4fafa]',
              )}
            >
              <span className={cn('h-2 w-2 rounded-full flex-none', on ? 'bg-nav-teal' : 'bg-nav-line')} aria-hidden />
              <span className="truncate">{r.full_name}</span>
              <span className="ml-auto font-head text-[10px] uppercase tracking-wide text-nav-faint">{t(roleKey(r.role))}</span>
            </button>
          );
        })}
      </div>

      <p className="m-0 mb-2 font-head text-[10.5px] font-bold tracking-[.12em] uppercase text-nav-slate">{t('f5r6b.new.firstMsg')}</p>
      <textarea
        rows={4}
        value={firstMessage}
        maxLength={4000}
        onChange={(e) => setFirstMessage(e.target.value)}
        className="w-full box-border px-3 py-2.5 mb-3 border-[1.5px] border-nav-line rounded-[10px] font-body text-[13.5px] leading-relaxed text-nav-navy bg-white resize-none focus-visible:outline-none focus-visible:border-nav-teal"
      />

      {create.error != null && (
        <div className="mb-3">
          <ErrorState
            icon={<TriangleAlert className="h-5 w-5" />}
            title={t('f5r6b.err.title')}
            body={friendlyCreateThreadError((create.error as Error).message)}
          />
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={selected.length === 0 || create.isPending}
          className={cn(
            'inline-flex items-center gap-2 min-h-[44px] px-5 rounded-lg border-[1.5px] font-head text-[12.5px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
            selected.length === 0 || create.isPending
              ? 'bg-nav-line border-nav-line text-white cursor-not-allowed'
              : 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover hover:border-nav-navyHover',
          )}
        >
          {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('f5r6b.new.start')}
        </button>
      </div>
    </div>
  );
}
